// Edit sheet for item_ammo_attributes (P1-18 PR B).
//
// Full-block edit — all seven ammo fields are editable per owner directive
// 2026-10-31. None feed compliance; freeform text has no cap.

import { AMMO_BULLET_TYPES, validateAmmoAttributes } from "@betterpos/domain";
import { useState } from "react";
import { readSessionToken } from "../../data/audit.ts";
import type { AmmoAttributes } from "../../data/catalog-attrs.ts";
import { type AmmoAttributesUpdate, updateItemAmmo } from "../../data/catalog-edits.ts";
import { AttrSheet, messageForEditError } from "./AttrSheet.tsx";
import { Field, NumberInput, Segmented, TextInput, Toggle } from "./SheetControls.tsx";

type Props = {
  itemId: string;
  current: AmmoAttributes;
  onSaved: (next: AmmoAttributesUpdate) => void;
  onCancel: () => void;
};

const BULLET_OPTIONS = AMMO_BULLET_TYPES.map((v) => ({
  value: v,
  label: v === "fmj" || v === "jhp" || v === "sp" || v === "hp" ? v.toUpperCase() : titleCase(v),
}));

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function AmmoAttrsSheet({ itemId, current, onSaved, onCancel }: Props) {
  const [manufacturer, setManufacturer] = useState(current.manufacturer);
  const [caliberGauge, setCaliberGauge] = useState(current.caliberGauge);
  const [grain, setGrain] = useState<number | null>(current.grain);
  const [bulletType, setBulletType] = useState<AmmoAttributes["bulletType"]>(current.bulletType);
  const [isHollowPoint, setIsHollowPoint] = useState(current.isHollowPoint);
  const [roundsPerBox, setRoundsPerBox] = useState<number | null>(current.roundsPerBox);
  const [muzzleVelocityFps, setMuzzleVelocityFps] = useState<number | null>(
    current.muzzleVelocityFps,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidate = {
    manufacturer,
    caliberGauge,
    grain,
    bulletType,
    isHollowPoint,
    roundsPerBox: roundsPerBox ?? 0,
    muzzleVelocityFps,
  };
  const validation = validateAmmoAttributes(candidate);
  const changed =
    manufacturer.trim() !== current.manufacturer ||
    caliberGauge.trim() !== current.caliberGauge ||
    grain !== current.grain ||
    bulletType !== current.bulletType ||
    isHollowPoint !== current.isHollowPoint ||
    roundsPerBox !== current.roundsPerBox ||
    muzzleVelocityFps !== current.muzzleVelocityFps;
  const canSave = validation.ok && changed && !saving;

  const save = async () => {
    if (!validation.ok) return;
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setSaving(true);
    setError(null);
    const res = await updateItemAmmo(token, itemId, {
      expectedVersion: current.version,
      attributes: validation.value,
    });
    setSaving(false);
    if (!res.ok) {
      setError(messageForEditError(res, "This field"));
      return;
    }
    onSaved(res.value);
  };

  return (
    <AttrSheet
      title="Edit ammo attributes"
      error={error}
      saving={saving}
      canSave={canSave}
      onCancel={onCancel}
      onSave={save}
    >
      <Field label="Manufacturer">
        <TextInput value={manufacturer} onChange={setManufacturer} disabled={saving} />
      </Field>
      <Field label="Caliber / gauge">
        <TextInput value={caliberGauge} onChange={setCaliberGauge} disabled={saving} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Grain">
          <NumberInput value={grain} onChange={setGrain} disabled={saving} placeholder="e.g. 115" />
        </Field>
        <Field label="Rounds / box">
          <NumberInput value={roundsPerBox} onChange={setRoundsPerBox} disabled={saving} />
        </Field>
      </div>
      <Field label="Bullet type">
        <Segmented
          value={bulletType}
          options={BULLET_OPTIONS}
          onChange={setBulletType}
          disabled={saving}
          nullable
        />
      </Field>
      <div className="flex items-center justify-between">
        <span className="text-[15px] text-slate-700">Hollow point</span>
        <Toggle
          value={isHollowPoint}
          onChange={setIsHollowPoint}
          disabled={saving}
          label="Hollow point"
        />
      </div>
      <Field label="Muzzle velocity (fps)">
        <NumberInput
          value={muzzleVelocityFps}
          onChange={setMuzzleVelocityFps}
          disabled={saving}
          placeholder="e.g. 1150"
        />
      </Field>
    </AttrSheet>
  );
}
